import { z } from 'zod';
import { FOOTER_SYSTEM_VARIABLE_NAMES } from './email-footer.js';
import { parseVariableDefinitions, VARIABLE_NAME } from './variable_definitions.js';

// ─────────────────────────────────────────────────────────────────
// Audience attributes
//
// An email list owns the *definitions* of the merge fields its audience can
// be personalised with (`first_name`, `plan`, …), stored on the list row as a
// VariableDefinitions record — the same shape, validation and wholesale-PUT
// write path as an email's own variables. So an attribute key is a mustache
// token like any other, and a subscriber's values for them are validated by
// `validateVariableValues` against the list it belongs to.
//
// The workspace then caches the union of those keys, grouped by list, on the
// workspace row. The cache exists because the two sides want different things:
//
//   · the list editor wants one list's full definitions (it has the row), and
//   · the block editor wants every key the workspace can merge, on every
//     keystroke of a `{{` picker, without fanning out over the lists.
//
// It is a cache and treated as one: the server rewrites it whenever a list (or
// its attributes) changes, and the read path rebuilds it when it is missing,
// so a stale row cannot outlive the write that caused it.
// ─────────────────────────────────────────────────────────────────

/** One list's contribution to the workspace's merge fields. */
export const WorkspaceAttributeGroupSchema = z.object({
  listId: z.string().uuid(),
  /** Carried so the picker can say where a key comes from. */
  listName: z.string(),
  /** Attribute keys, sorted and de-duplicated. */
  keys: z.array(z.string()),
});
export type WorkspaceAttributeGroup = z.infer<typeof WorkspaceAttributeGroupSchema>;

export const WorkspaceAttributeCacheSchema = z.object({
  /** When the cache was last rebuilt. Null only for a hand-written row. */
  updatedAt: z.string().nullable(),
  /** One entry per list, by list name. Lists with no attributes are omitted. */
  lists: z.array(WorkspaceAttributeGroupSchema),
});
export type WorkspaceAttributeCache = z.infer<typeof WorkspaceAttributeCacheSchema>;

/** An empty cache — what a workspace with no attributed lists looks like. */
export const EMPTY_WORKSPACE_ATTRIBUTE_CACHE: WorkspaceAttributeCache = {
  updatedAt: null,
  lists: [],
};

/** Anything that carries a list's identity and its stored attributes. */
export interface AttributedList {
  id: string;
  name: string;
  /** Stored jsonb — read tolerantly, so a malformed entry cannot poison the cache. */
  attributes?: unknown;
}

/**
 * The attribute keys a list defines, sorted. Keys that are not valid
 * identifiers are dropped by the reader, because they could never be a
 * mustache token (`{{my key}}` is a literal, not a lookup).
 */
export function attributeKeysForList(attributes: unknown): string[] {
  return Object.keys(parseVariableDefinitions(attributes)).sort();
}

/**
 * Build the workspace cache from the workspace's lists.
 *
 * Lists with no attributes are left out rather than recorded empty: the cache
 * answers "what can I merge", and an entry with no keys answers nothing.
 * Ordering is by list name so the stored value is stable — an unchanged
 * workspace rewrites the same bytes, which keeps the column diffable.
 */
export function buildWorkspaceAttributeCache(
  lists: readonly AttributedList[],
  now: Date = new Date(),
): WorkspaceAttributeCache {
  const groups = lists
    .map((list) => ({
      listId: list.id,
      listName: list.name,
      keys: attributeKeysForList(list.attributes),
    }))
    .filter((group) => group.keys.length > 0)
    .sort((a, b) => a.listName.localeCompare(b.listName) || a.listId.localeCompare(b.listId));

  return { updatedAt: now.toISOString(), lists: groups };
}

/** Read a stored cache tolerantly — a malformed row degrades to "nothing cached". */
export function parseWorkspaceAttributeCache(raw: unknown): WorkspaceAttributeCache | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const source = raw as Record<string, unknown>;
  if (!Array.isArray(source['lists'])) return null;

  const lists: WorkspaceAttributeGroup[] = [];
  for (const entry of source['lists']) {
    if (!entry || typeof entry !== 'object') continue;
    const group = entry as Record<string, unknown>;
    const listId = group['listId'];
    const listName = group['listName'];
    const keys = group['keys'];
    if (typeof listId !== 'string' || typeof listName !== 'string' || !Array.isArray(keys))
      continue;
    lists.push({
      listId,
      listName,
      keys: keys
        .filter((key): key is string => typeof key === 'string')
        .filter((key) => VARIABLE_NAME.test(key) && key !== '__proto__'),
    });
  }

  return {
    updatedAt: typeof source['updatedAt'] === 'string' ? source['updatedAt'] : null,
    lists,
  };
}

// ─────────────────────────────────────────────────────────────────
// Available template tokens
// ─────────────────────────────────────────────────────────────────

/**
 * Names a document may **not** define, because the build resolves them from
 * live system data and they must win: `unsubscribe_url` above all (a
 * workspace must not be able to shadow its own opt-out).
 */
export const RESERVED_TEMPLATE_TOKENS: readonly string[] = FOOTER_SYSTEM_VARIABLE_NAMES;

export interface AvailableTemplateTokensInput {
  /** Keys of the document's own definitions (`emails.variable_definition`). */
  documentVariables?: readonly string[] | undefined;
  /** Attribute keys from the workspace cache, in any grouping. */
  workspaceAttributes?: readonly string[] | undefined;
  /** Defaults to {@link RESERVED_TEMPLATE_TOKENS}. */
  reserved?: readonly string[] | undefined;
}

/**
 * The `{{…}}` tokens the block editor may offer for a document: its own
 * variables plus the workspace's audience attributes, minus the reserved
 * system names.
 *
 * One function so the several surfaces that offer tokens — the inline
 * mention picker, the image/OTP inspectors — cannot drift: a token that is
 * offered somewhere must be offered everywhere, and a reserved name must
 * never be offered at all (picking it would produce a token the build
 * silently overwrites).
 *
 * Sorted and de-duplicated, because the picker renders the list in order and
 * a key defined on two lists is still one token.
 */
export function availableTemplateTokens(input: AvailableTemplateTokensInput = {}): string[] {
  const reserved = new Set(input.reserved ?? RESERVED_TEMPLATE_TOKENS);
  const tokens = new Set<string>();

  for (const name of [...(input.documentVariables ?? []), ...(input.workspaceAttributes ?? [])]) {
    if (typeof name !== 'string') continue;
    if (name === '__proto__' || !VARIABLE_NAME.test(name)) continue;
    if (reserved.has(name)) continue;
    tokens.add(name);
  }

  return [...tokens, ...RESERVED_TEMPLATE_TOKENS].sort();
}

/** Flatten a cache into just its keys (the token list's attribute half). */
export function attributeKeysFromCache(
  cache: WorkspaceAttributeCache | null | undefined,
): string[] {
  if (!cache) return [];
  return cache.lists.flatMap((group) => group.keys);
}
